import api from "../api/axios";
import { getNativePlatform } from "../native/runtime";

export const ONBOARDING_CARD_IDS = ["welcome", "culture", "respect", "responsibility", "preferences", "come_alone", "first_step"];
const CONTEXT_KEY = "vg_registration_journey_v1";
const QUEUE_PREFIX = "vg_registration_telemetry_v1:";
const MAX_AGE = 7 * 24 * 60 * 60 * 1000;
const pending = new Map();
const sending = new Map();
const id = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  const bytes = new Uint8Array(16);
  if (window.crypto?.getRandomValues) window.crypto.getRandomValues(bytes);
  else bytes.forEach((_, index) => { bytes[index] = Math.floor(Math.random() * 256); });
  bytes[6] = (bytes[6] & 15) | 64;
  bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};
const read = (storage, key, fallback) => {
  try { return JSON.parse(window[storage].getItem(key)) || fallback; } catch { return fallback; }
};
const write = (storage, key, value) => {
  try { window[storage].setItem(key, JSON.stringify(value)); } catch { /* Private mode/quota: keep in memory. */ }
};

export function getRegistrationJourney(eventId = null) {
  const journey = read("sessionStorage", CONTEXT_KEY, null);
  if (!journey || Date.now() - Date.parse(journey.started_at) > MAX_AGE) return null;
  if (eventId !== null && Number(journey.offered_event_id) !== Number(eventId)) return null;
  return journey;
}

export function beginRegistrationJourney({ userId, language, totalSteps, eventId, pathname }) {
  // Preview/debug pages must never pollute real conversion cohorts, even when logged in.
  if (pathname !== "/registration/onboarding") {
    try { sessionStorage.removeItem(CONTEXT_KEY); } catch { /* Ignore storage restrictions. */ }
    return null;
  }
  if (!userId) return null;
  const previous = getRegistrationJourney();
  if (previous?.user_id === userId && !previous.cards_finished) return previous;
  const journey = {
    session_id: id(), user_id: userId, version: "registration_cards_v1",
    platform: getNativePlatform(), language: String(language || "").slice(0, 16),
    total_steps: totalSteps, offered_event_id: eventId || null,
    started_at: new Date().toISOString(),
  };
  write("sessionStorage", CONTEXT_KEY, journey);
  return journey;
}

function queueFor(journey) {
  const key = QUEUE_PREFIX + journey.session_id;
  if (!pending.has(key)) {
    const stored = read("localStorage", key, []);
    pending.set(key, Array.isArray(stored) ? stored : []);
  }
  return { key, rows: pending.get(key) };
}

function persistQueue(key, rows) {
  if (rows.length) write("localStorage", key, rows);
  else { try { localStorage.removeItem(key); } catch { /* Ignore storage restrictions. */ } }
}

export function flushRegistrationAnalytics(journey) {
  if (!journey) return Promise.resolve();
  const { key, rows } = queueFor(journey);
  if (sending.has(key)) return sending.get(key);
  if (!rows.length) return Promise.resolve();
  const delivery = (async () => {
    while (rows.length) {
      const batch = rows.slice(0, 40);
      try {
        await api.post("/accounts/registration-onboarding/analytics/", { ...journey, events: batch }, {
          // Use the normal CSRF/JWT interceptors, also for a final pagehide flush.
          adapter: "fetch", fetchOptions: { keepalive: true }, timeout: 10000,
          meta: { suppressGlobalError: true, suppressUnauthorizedRedirect: true },
        });
        const delivered = new Set(batch.map(row => row.id));
        for (let i = rows.length - 1; i >= 0; i -= 1) {
          if (delivered.has(rows[i].id)) rows.splice(i, 1);
        }
        persistQueue(key, rows);
      } catch (error) {
        // Invalid/other-account payloads cannot succeed on retry. Retry network/5xx/429
        // later using the SAME UUIDs, so a lost HTTP response never double-counts.
        if ([400, 401, 403, 404].includes(error?.response?.status)) {
          const rejected = new Set(batch.map(row => row.id));
          for (let i = rows.length - 1; i >= 0; i -= 1) {
            if (rejected.has(rows[i].id)) rows.splice(i, 1);
          }
          persistQueue(key, rows);
        }
        break;
      }
    }
  })();
  sending.set(key, delivery);
  delivery.finally(() => sending.delete(key));
  return delivery;
}

export function trackRegistrationAction(journey, action, fields = {}) {
  if (!journey) return;
  const { key, rows } = queueFor(journey);
  rows.push({ id: id(), action, occurred_at: new Date().toISOString(), ...fields });
  // Bounded local retention; checkpoints are cumulative so older ones can be discarded.
  if (rows.length > 400) {
    const checkpoint = rows.findIndex(row => row.action.endsWith("_checkpoint"));
    rows.splice(checkpoint < 0 ? 0 : checkpoint, 1);
  }
  write("localStorage", key, rows);
  if (action === "cards_start" || action === "cards_newcomer") {
    write("sessionStorage", CONTEXT_KEY, {
      ...journey, cards_finished: true,
      offered_event_id: fields.target_event_id || journey.offered_event_id,
    });
  }
  void flushRegistrationAnalytics(journey);
}

// Retry after connectivity returns even if the user has already left onboarding.
// The authenticated server rejects a context belonging to a different account.
if (typeof window !== "undefined") {
  window.addEventListener("online", () => { void flushRegistrationAnalytics(getRegistrationJourney()); });
}

export function trackNewcomerAction(eventId, action) {
  if (window.location.pathname.startsWith("/local-debug/") || new URLSearchParams(window.location.search).get("newcomer_intro") === "preview") return;
  trackRegistrationAction(getRegistrationJourney(eventId), action);
}

// A visit is one continuous card/page mount. Checkpoints contain cumulative active
// time: aggregate MAX(active_ms) per visit, then SUM visits, never SUM checkpoints.
export function createRegistrationVisit(journey, kind, step = "") {
  const visitId = id();
  let activeMs = 0;
  let activeSince = null;
  let closed = false;
  const sample = () => {
    if (activeSince !== null) {
      const now = performance.now();
      activeMs += Math.max(0, now - activeSince);
      activeSince = now;
    }
    return { visit_id: visitId, step, active_ms: Math.round(activeMs) };
  };
  const emit = (action, fields = {}) => trackRegistrationAction(journey, action, { ...sample(), ...fields });
  return {
    start(visible) {
      activeSince = visible ? performance.now() : null;
      emit(`${kind}_view`);
    },
    visible(visible) {
      if (closed) return;
      sample();
      activeSince = visible ? performance.now() : null;
      emit(`${kind}_checkpoint`);
    },
    checkpoint() { if (!closed) emit(`${kind}_checkpoint`); },
    action(action, fields) { if (!closed) emit(action, fields); },
    close() {
      if (closed) return;
      emit(`${kind}_leave`);
      closed = true;
      activeSince = null;
    },
  };
}
